import { t } from "@lingui/macro";
import { atom } from "jotai";

import { reorderPrivateRounds } from "@/features/BookingRounds/utils/reorderPrivateRounds";

import {
  CreateMultiSlotBookingsInput,
  CreateCustomerRoundInput,
  CreateMemberRoundInput,
  CreateGuestRoundInput,
  NewGuestRoundInput,
} from "../../../hooks/useCreateNewMultiSlotBooking/__generated__/useCreateNewMultiSlotBookingMutation.graphql";
import { BookingWizard } from "../../../types";
import { BookingWizardDataAtom } from "../../stores";
import { processPrivateDurationBookingRounds } from "../../utils/processPrivateDurationBookingRounds";

type CustomerRoundInput = CreateCustomerRoundInput;

type PrivateRounds = {
  memberRounds: CreateMemberRoundInput[];
  guestRounds: CreateGuestRoundInput[];
  newGuestRounds: NewGuestRoundInput[];
};

const buildCustomerRounds = (
  rounds: BookingWizard.DurationRound[] | undefined,
  groupSize: number | undefined
): CustomerRoundInput[] => {
  const base: CustomerRoundInput[] = (rounds ?? []).map((round, index) => ({
    index,
    playerId: round.player.id,
  }));

  if (groupSize && base.length < groupSize) {
    const remainingRounds = groupSize - base.length;
    const newRounds: CustomerRoundInput[] = Array.from(
      { length: remainingRounds },
      (_, i) => ({
        index: base.length + i,
        playerId: null,
      })
    );
    base.push(...newRounds);
  }

  return base;
};

const buildPrivateRounds = (
  rounds: BookingWizard.DurationRound[] | undefined,
  ownerClientId: string
): PrivateRounds => {
  const memberRounds =
    processPrivateDurationBookingRounds<CreateMemberRoundInput>(rounds, [
      "member",
      "memberHold",
    ]);

  const guestRounds =
    processPrivateDurationBookingRounds<CreateGuestRoundInput>(rounds, [
      "guest",
      "unknownGuest",
    ]);

  const newGuestRounds =
    processPrivateDurationBookingRounds<NewGuestRoundInput>(
      rounds,
      ["newGuest"],
      (round) => {
        const safeFirst = round.player?.firstName?.trim() ?? "";
        const safeLast = round.player?.lastName?.trim() ?? "";
        const fullName = [safeFirst, safeLast].filter(Boolean).join(" ").trim();
        return {
          name: fullName.length > 0 ? fullName : t`New Guest`,
        };
      }
    );

  return reorderPrivateRounds({
    memberRounds,
    guestRounds,
    newGuestRounds,
    ownerClientId,
  });
};

export const createMultiSlotBookingsInputAtom =
  atom<CreateMultiSlotBookingsInput | null>((get) => {
    const bookingWizardData = get(BookingWizardDataAtom);
    if (!bookingWizardData || bookingWizardData.type !== "DurationBooking") {
      return null;
    }
    const { startSlotId, ownerClientId, rounds, groupSize } = bookingWizardData;

    const durationProperties = {
      durationMins: bookingWizardData.durationMins,
      playerRateId: bookingWizardData.playerRateId ?? null,
      rateId: bookingWizardData.bookingRateId ?? null,
      isPrivate: bookingWizardData.isPrivate,
    };

    const paymentCardOnFileId =
      bookingWizardData.paymentCardOnFileId ?? undefined;

    if (!startSlotId) {
      return null;
    }
    const bookingInput: CreateMultiSlotBookingsInput = {
      bookings: [
        {
          ...(durationProperties.isPrivate
            ? buildPrivateRounds(rounds, ownerClientId)
            : {
                customerRounds: buildCustomerRounds(rounds, groupSize),
              }),
          startSlotId,
          durationMins: durationProperties.durationMins,
          rateId: durationProperties.rateId,
          paymentCardOnFileId,
          ownerClientId,
          isDraft: true,
        },
      ],
      notifyMember: true,
    };

    return bookingInput;
  });
